import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import toast from "react-hot-toast";

import { useAddUser, useListOrganisations } from "../../../store";

import { User, UserType } from "../../../types";
import { useState } from "react";
import { UserInput } from "../../../../../input-types";

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  onClose,
}: InviteUserModalProps) => {
  const [inProgress, setInProgress] = useState<boolean>(false);
  const { data: organisations = [], isLoading: isOrganisationsLoading } = useListOrganisations();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      organisationIds: [],
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Invalid email address")
        .required("Email is required"),
      organisationIds: Yup.array().of(Yup.string().trim()),
    }),
    onSubmit: (values) => {
      const { email, organisationIds } = values;

      const createdUser: UserInput = {
        email,
        organisationIds,
        userType: UserType.User,
      };

      submitInviteUserForm(createdUser);
    },
  });

  const onInviteSuccess = () => {
    toast.success("Invitation sent successfully");
    setInProgress(false);
    onClose();
  };

  const onInviteError = (err: any) => {
    console.error(err);

    if (err?.message && err?.message.includes("block")) {
      toast.error("Cannot add blocked user");
    } else {
      toast.error("Failed to send invitation");
    }

    setInProgress(false);
    onClose();
  };

  const { mutate: addUser } = useAddUser(onInviteSuccess, onInviteError);

  const submitInviteUserForm = (newUser: UserInput) => {
    setInProgress(true);
    addUser(newUser);
  };

  const updateOrganisationsIds = (selectedOrganisationIds: string[]) => {
    formik.setFieldTouched("organisationIds", true);
    formik.setFieldValue("organisationIds", selectedOrganisationIds);
  };

  const cancel = () => {
    formik.resetForm();
    onClose();
  };

  const isFormValid = () => {
    return (
      formik.values.email.trim() &&
      Object.keys(formik.errors).length === 0
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle component={"div"}>
        <Typography variant="h3">Invite User</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              variant="outlined"
              autoComplete="email"
              size="small"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <Stack direction={"column"}>
              <FormControl fullWidth size="small" disabled={isOrganisationsLoading}>
                <InputLabel id="invite-user-organisation-label">Teams (Optional)</InputLabel>
                <Select
                  multiple
                  labelId="invite-user-organisation-label"
                  id="invite-user-organisation"
                  value={formik.values.organisationIds}
                  label="Organisation (Optional)"
                  onChange={(event) =>
                    updateOrganisationsIds(
                      typeof event.target.value === "string"
                        ? [event.target.value]
                        : event.target.value,
                    )
                  }
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {selected.map((organisationId) => {
                        const organisation = organisations.find(
                          (item) => item.organisationId === organisationId,
                        );

                        if (!organisation) {
                          return null;
                        }

                        return (
                          <Chip
                            key={organisation.organisationId}
                            label={organisation.name}
                            size="small"
                            sx={{
                              borderRadius: 1,
                              border: `1px solid ${organisation.color || "#E5E7EB"}`,
                              backgroundColor: `${organisation.color || "#E5E7EB"}22`,
                              color: "text.primary",
                              height: 24,
                              fontSize: "0.75rem",
                            }}
                          />
                        );
                      })}
                    </Stack>
                  )}
                  sx={{ minHeight: 38 }}
                >
                  {organisations.map((organisation) => (
                    <MenuItem
                      key={organisation.organisationId}
                      value={organisation.organisationId}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 1,
                            backgroundColor: organisation.color || "#E5E7EB",
                            border: "1px solid rgba(0,0,0,0.12)",
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText primary={organisation.name} />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formik.touched.organisationIds && formik.errors.organisationIds && (
                <Typography color="error">{formik.errors.organisationIds}</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button fullWidth variant="outlined" onClick={cancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={inProgress || !isFormValid()}
              >
                {inProgress ? "Loading..." : "Invite"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserModal;
